'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { LeadData } from './PitchDeck';
import { User, Phone, Mail, Check, Lock, AlertCircle, MapPin, ShieldCheck } from 'lucide-react';
import OrderSuccessModal from './OrderSuccessModal';

interface Props {
  leadData: LeadData;
  path: 'A' | 'B' | null;
  selectedPackage: string;
  selectedAutoship: string;
  onSelectPackage: (pkg: string) => void;
  onSelectAutoship: (auto: string) => void;
  onOrderSubmitted?: () => void;
}

interface OrderFormData extends LeadData {
  sponsorName: string;
  username: string;
  password: string;
  dateOfBirth: string;
  shipFirstName?: string;
  shipLastName?: string;
  shipAddress?: string;
  shipCity?: string;
  shipState?: string;
  shipZip?: string;
}

const pathBOptions = [
  { id: 'retail-1sleeve', label: 'Retail — 1 Sleeve X39', price: '$149.95', note: 'One-time order · 90-day money back' },
  { id: 'pc-1sleeve', label: 'Preferred Customer — 1 Sleeve/Month', price: '$99.95/mo', note: 'Requires autoship' },
  { id: 'pc-2sleeve', label: 'Preferred Customer — 2 Sleeves/Month', price: '$199.90/mo', note: 'Requires autoship' },
];

const pathAPackages = [
  { id: 'pkg-core', label: 'Core Enrollment', price: '$295', note: '3 Sleeves of X39', bestValue: false },
  { id: 'pkg-advanced', label: 'Advanced Enrollment', price: '$535', note: '6 Sleeves of X39', bestValue: false },
  { id: 'pkg-advanced-plus', label: 'Advanced Plus Enrollment', price: '$975', note: '11 Sleeves of X39', bestValue: false },
  { id: 'pkg-premium', label: 'Premium Enrollment', price: '$1,750', note: '20 Sleeves of X39', bestValue: true },
];

const autoshipOptions = [
  { id: 'auto-1', label: '1 Sleeve/Month — $99.95/mo', note: 'Keep subscription active', suggested: false },
  { id: 'auto-2', label: '2 Sleeves/Month — $199.90/mo', note: 'Potential to max comp plan', suggested: true },
];

const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{12,}$/;

export default function Slide9OrderForm({
  leadData,
  path,
  selectedPackage,
  selectedAutoship,
  onSelectPackage,
  onSelectAutoship,
  onOrderSubmitted,
}: Props) {
  const [showModal, setShowModal] = useState(false);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [localPackage, setLocalPackage] = useState(selectedPackage || (path === 'A' ? 'pkg-premium' : ''));
  const [localAutoship, setLocalAutoship] = useState(selectedAutoship || (path === 'A' ? 'auto-2' : ''));
  const [autoshipError, setAutoshipError] = useState(false);
  const [submittedData, setSubmittedData] = useState<OrderFormData | null>(null);
  const [shipDifferent, setShipDifferent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<OrderFormData>({
    defaultValues: {
      firstName: leadData.firstName,
      lastName: leadData.lastName,
      phone: leadData.phone,
      email: leadData.email,
      dateOfBirth: '',
      sponsorName: '',
      username: '',
      password: '',
    },
  });

  const onSubmit = async (data: OrderFormData) => {
    if (path === 'A' && !localAutoship) {
      setAutoshipError(true);
      return;
    }
    setAutoshipError(false);

    try {
      await fetch('/api/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'order',
          data: {
            ...data,
            selectedPackage: localPackage,
            selectedAutoship: localAutoship,
            path,
            shipDifferent,
            leadId: leadData.leadId,
          },
        }),
      });
    } catch (err) {
      console.error('Order notification failed:', err);
    }

    setSubmittedData(data);
    setOrderSubmitted(true);
    setShowModal(true);
    if (onOrderSubmitted) {
      onOrderSubmitted();
    }
  };

  const handleSelectPackage = (id: string) => {
    if (orderSubmitted) return;
    setLocalPackage(id);
    onSelectPackage(id);
  };

  const packageOptions = path === 'A' ? pathAPackages : pathBOptions;

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-16 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 70% 40% at 50% 0%, rgba(0,168,255,0.07) 0%, transparent 70%)',
        }}
      />

      {/* Overlay to disable page after submission */}
      {orderSubmitted && !showModal && (
        <div className="fixed inset-0 z-30 bg-background/60 backdrop-blur-sm pointer-events-auto" />
      )}

      <div className="max-w-3xl w-full mx-auto fade-in relative z-10">
        <div className="text-center mb-10">
          <p className="text-primary text-sm font-semibold tracking-widest uppercase mb-3">
            Final Step
          </p>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground leading-tight mb-4">
            Complete Your <span className="gradient-text">Order</span>
          </h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

          {/* Package Selection — FIRST */}
          <div className="glass-card p-6">
            <h3 className="text-foreground font-bold text-lg mb-4">
              {path === 'A' ? 'Select Your Enrollment Package' : 'Select Your Order'}
            </h3>
            <div className="space-y-3">
              {packageOptions.map((opt) => {
                const isSelected = localPackage === opt.id;
                return (
                  <button
                    type="button"
                    key={opt.id}
                    onClick={() => handleSelectPackage(opt.id)}
                    disabled={orderSubmitted}
                    className="w-full text-left p-4 rounded-xl border transition-all duration-200 flex items-center justify-between gap-4"
                    style={{
                      background: isSelected ? 'rgba(0,168,255,0.1)' : 'rgba(255,255,255,0.03)',
                      borderColor: isSelected ? 'rgba(0,168,255,0.4)' : 'rgba(255,255,255,0.08)',
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                        style={{ borderColor: isSelected ? 'var(--primary)' : 'rgba(255,255,255,0.2)' }}
                      >
                        {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                      </div>
                      <div>
                        <p className="text-foreground font-semibold text-sm">{opt.label}</p>
                        <p className="text-muted-foreground text-xs mt-0.5">{opt.note}</p>
                        {opt.bestValue && (
                          <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-500 to-amber-400 text-black">
                            Best Value
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="text-primary font-bold text-base tabular-nums block">{opt.price}</span>
                      <span className="text-muted-foreground text-xs">+ tax &amp; shipping</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Autoship (Path A only) — SECOND */}
          {path === 'A' && (
            <div className="glass-card p-6">
              <h3 className="text-foreground font-bold text-lg mb-1">Monthly Autoship <span className="text-red-400">*</span></h3>
              <p className="text-muted-foreground text-xs mb-4">Requires active autoship to maintain wholesale pricing</p>
              <div className="space-y-3">
                {autoshipOptions.map((opt) => {
                  const isSelected = localAutoship === opt.id;
                  return (
                    <button
                      type="button"
                      key={opt.id}
                      onClick={() => { if (!orderSubmitted) { setLocalAutoship(opt.id); setAutoshipError(false); onSelectAutoship(opt.id); } }}
                      disabled={orderSubmitted}
                      className="w-full text-left p-4 rounded-xl border transition-all duration-200 flex items-center justify-between gap-4"
                      style={{
                        background: isSelected ? 'rgba(0,168,255,0.1)' : 'rgba(255,255,255,0.03)',
                        borderColor: isSelected ? 'rgba(0,168,255,0.4)' : autoshipError ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.08)',
                        cursor: orderSubmitted ? 'default' : 'pointer',
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                          style={{ borderColor: isSelected ? 'var(--primary)' : 'rgba(255,255,255,0.2)' }}
                        >
                          {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                        </div>
                        <div>
                          {opt.suggested && (
                            <span className="block text-xs font-bold text-accent mb-0.5">Suggested</span>
                          )}
                          <p className="text-foreground font-semibold text-sm">{opt.label}</p>
                          <p className="text-muted-foreground text-xs">{opt.note}</p>
                          <p className="text-muted-foreground text-xs">+ tax &amp; shipping</p>
                        </div>
                      </div>
                      {isSelected && <Check size={16} className="text-primary flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
              {autoshipError && (
                <div className="flex items-center gap-2 mt-3 text-red-400 text-sm">
                  <AlertCircle size={14} />
                  <span>Please select a monthly autoship option to continue.</span>
                </div>
              )}
            </div>
          )}

          {/* Personal Info — THIRD */}
          <div className="glass-card p-6">
            <h3 className="text-foreground font-bold text-lg mb-4 flex items-center gap-2">
              <User size={18} className="text-primary" />
              Your Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">First Name</label>
                <input {...register('firstName', { required: 'Required' })} className="input-field" disabled={orderSubmitted} />
                {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Last Name</label>
                <input {...register('lastName', { required: 'Required' })} className="input-field" disabled={orderSubmitted} />
                {errors.lastName && <p className="text-red-400 text-xs mt-1">{errors.lastName.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  <Phone size={13} className="inline mr-1" />
                  Mobile Number
                </label>
                <input {...register('phone', { required: 'Required', pattern: { value: /^[\d\s\-+()]{7,}$/, message: 'Enter a valid phone number' } })} className="input-field" type="tel" disabled={orderSubmitted} />
                {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  <Mail size={13} className="inline mr-1" />
                  Email Address
                </label>
                <input {...register('email', { required: 'Required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' } })} className="input-field" type="email" disabled={orderSubmitted} />
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Date of Birth <span className="text-red-400">*</span>
                </label>
                <input
                  {...register('dateOfBirth' as any, { required: 'Date of birth is required' })}
                  className="input-field"
                  type="date"
                  disabled={orderSubmitted}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                />
                {(errors as any).dateOfBirth && <p className="text-red-400 text-xs mt-1">{(errors as any).dateOfBirth.message}</p>}
              </div>
            </div>

            {/* Billing Address */}
            <div className="mt-4 space-y-3">
              <h4 className="text-foreground font-semibold text-sm">Billing Address</h4>
              <input
                {...register('address' as any, { required: 'Billing address is required' })}
                className="input-field"
                placeholder="Street Address"
                disabled={orderSubmitted}
              />
              {(errors as any).address && <p className="text-red-400 text-xs mt-1">{(errors as any).address.message}</p>}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <input
                  {...register('city' as any, { required: 'City is required' })}
                  className="input-field"
                  placeholder="City"
                  disabled={orderSubmitted}
                />
                <input
                  {...register('state' as any, { required: 'State is required' })}
                  className="input-field"
                  placeholder="State"
                  disabled={orderSubmitted}
                />
                <input
                  {...register('zip' as any, { required: 'ZIP is required' })}
                  className="input-field col-span-2 sm:col-span-1"
                  placeholder="ZIP Code"
                  disabled={orderSubmitted}
                />
              </div>
            </div>

            {/* Shipping address checkbox */}
            <div className="mt-4">
              <label className="flex items-center gap-3 cursor-pointer select-none group">
                <div
                  onClick={() => { if (!orderSubmitted) setShipDifferent(!shipDifferent); }}
                  className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 cursor-pointer"
                  style={{
                    borderColor: shipDifferent ? 'var(--primary)' : 'rgba(255,255,255,0.3)',
                    background: shipDifferent ? 'var(--primary)' : 'transparent',
                  }}
                >
                  {shipDifferent && <Check size={12} className="text-white" />}
                </div>
                <span
                  className="text-sm text-foreground cursor-pointer"
                  onClick={() => { if (!orderSubmitted) setShipDifferent(!shipDifferent); }}
                >
                  My shipping address is different from my billing address
                </span>
              </label>
            </div>

            {/* Separate Shipping Address Section */}
            {shipDifferent && (
              <div className="mt-4 p-4 rounded-xl border space-y-3" style={{ borderColor: 'rgba(0,168,255,0.3)', background: 'rgba(0,168,255,0.04)' }}>
                <h4 className="text-foreground font-semibold text-sm flex items-center gap-2">
                  <MapPin size={14} className="text-primary" />
                  Shipping Address
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">First Name</label>
                    <input
                      {...register('shipFirstName', { required: shipDifferent ? 'Required' : false })}
                      className="input-field"
                      disabled={orderSubmitted}
                    />
                    {errors.shipFirstName && <p className="text-red-400 text-xs mt-1">{errors.shipFirstName.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Last Name</label>
                    <input
                      {...register('shipLastName', { required: shipDifferent ? 'Required' : false })}
                      className="input-field"
                      disabled={orderSubmitted}
                    />
                    {errors.shipLastName && <p className="text-red-400 text-xs mt-1">{errors.shipLastName.message}</p>}
                  </div>
                </div>
                <input
                  {...register('shipAddress', { required: shipDifferent ? 'Shipping address is required' : false })}
                  className="input-field"
                  placeholder="Street Address"
                  disabled={orderSubmitted}
                />
                {errors.shipAddress && <p className="text-red-400 text-xs mt-1">{errors.shipAddress.message}</p>}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <input
                    {...register('shipCity', { required: shipDifferent ? 'Required' : false })}
                    className="input-field"
                    placeholder="City"
                    disabled={orderSubmitted}
                  />
                  <input
                    {...register('shipState', { required: shipDifferent ? 'Required' : false })}
                    className="input-field"
                    placeholder="State"
                    disabled={orderSubmitted}
                  />
                  <input
                    {...register('shipZip', { required: shipDifferent ? 'Required' : false })}
                    className="input-field col-span-2 sm:col-span-1"
                    placeholder="ZIP Code"
                    disabled={orderSubmitted}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Account Creation — FOURTH */}
          <div className="glass-card p-6 border-accent/20" style={{ background: 'rgba(0,229,255,0.04)' }}>
            <h3 className="text-foreground font-bold text-lg mb-1 flex items-center gap-2">
              <Lock size={18} className="text-accent" />
              Create Your Account
            </h3>
            <p className="text-muted-foreground text-xs mb-4">Set up your login credentials for your LifeWave account.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Username <span className="text-red-400">*</span></label>
                <input
                  {...register('username', { required: 'Username is required', minLength: { value: 3, message: 'Minimum 3 characters' } })}
                  className="input-field"
                  placeholder="Typically first name and last name"
                  autoComplete="username"
                  disabled={orderSubmitted}
                />
                {errors.username && <p className="text-red-400 text-xs mt-1">{errors.username.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Password <span className="text-red-400">*</span></label>
                <input
                  {...register('password', {
                    required: 'Password is required',
                    validate: (val) =>
                      PASSWORD_REGEX.test(val) ||
                      'Min 12 chars, 1 uppercase, 1 number, 1 special character',
                  })}
                  className="input-field"
                  type="text"
                  autoComplete="new-password"
                  disabled={orderSubmitted}
                />
                {errors.password ? (
                  <p className="text-red-400 text-xs mt-1">{errors.password.message}</p>
                ) : (
                  <p className="text-muted-foreground text-xs mt-1">Min 12 chars · 1 uppercase · 1 number · 1 special character</p>
                )}
              </div>
            </div>
          </div>

          {/* Sponsor Info — FIFTH */}
          <div className="glass-card p-6 border-primary/20" style={{ background: 'rgba(0,168,255,0.04)' }}>
            <h3 className="text-foreground font-bold text-base mb-3">Who shared X39 with you? <span className="text-red-400">*</span></h3>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Name</label>
              <input
                {...register('sponsorName', { required: 'Please enter who shared X39 with you' })}
                className="input-field"
                placeholder="Enter their name"
                disabled={orderSubmitted}
              />
              {errors.sponsorName && <p className="text-red-400 text-xs mt-1">{errors.sponsorName.message}</p>}
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting || orderSubmitted}
            className="btn-primary w-full flex items-center justify-center gap-2 text-lg py-5"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Submitting...
              </span>
            ) : orderSubmitted ? (
              <>
                <Check size={20} />
                Order Submitted
              </>
            ) : (
              <>
                Claim My Order Now
                <Check size={20} />
              </>
            )}
          </button>
          <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5 mt-3">
            <ShieldCheck size={13} className="text-blue-400" />
            Your information is encrypted and secure
          </p>
        </form>
      </div>

      {showModal && (
        <OrderSuccessModal
          firstName={leadData.firstName || 'Friend'}
          email={submittedData?.email || leadData.email || ''}
          phone={submittedData?.phone || leadData.phone || ''}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}